import { useCallback } from 'react';

import { useDebugLogger } from '@/lib/debug/debug-logger';

import { Analytics } from './analytics';

import type { PageViewFn, TrackFn } from './types';

/**
 * React hook for analytics that integrates with debug mode
 *
 * Provides a type-safe interface for tracking events, pageviews, and user properties
 * across all configured analytics destinations (Amplitude, Console). All operations
 * include optional debug logging when debug mode is enabled.
 *
 * **Key Features:**
 * - Type-safe event tracking with payload validation
 * - Automatic debug logging integration
 * - Environment-aware behavior (development vs production)
 * - Error isolation for app stability
 *
 * **Usage:**
 * ```tsx
 * const { track, pageview, setUser, setInternalUser } = useAnalytics();
 *
 * // Track a custom event
 * track('ask_question', { feature: 'ask' });
 *
 * // Track a pageview
 * pageview({ path: '/dashboard', title: 'Dashboard' });
 *
 * // Set user properties
 * setUser({ userType: 'admin', tenantId: '123' });
 *
 * // Configure for internal users
 * setInternalUser({ id: '123', admin: true, saas_tenant_id: 'tenant_1' });
 * ```
 *
 * **Related Components:**
 * - {@link Analytics} - Direct analytics singleton for non-React contexts
 * - {@link useDebugLogger} - Debug logging hook used internally
 *
 * @returns Object containing analytics methods with debug logging integration
 * @returns {TrackFn} track - Function to track typed events with optional payload
 * @returns {PageViewFn} pageview - Function to track page navigation events
 * @returns {Function} setUser - Function to set user properties across all destinations
 * @returns {Function} setInternalUser - Function to configure analytics for internal users
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { track } = useAnalytics();
 *
 *   const handleButtonClick = () => {
 *     track('my_feature_modal_opened', { feature: 'my_feature' });
 *   };
 *
 *   return <button onClick={handleButtonClick}>Submit</button>;
 * }
 * ```
 */
export const useAnalytics = () => {
  const debugLogger = useDebugLogger('useAnalytics');

  /**
   * Track an event
   *
   * Sends an event to all configured analytics destinations with type-safe
   * payload validation. Events are automatically logged in debug mode.
   *
   * **Type Safety:**
   * - Event names must be valid keys from the EventPayloads interface
   * - Payload types are automatically inferred based on the event name
   * - Invalid event names or payloads will cause TypeScript errors
   *
   * **Error Handling:**
   * - Failures in individual destinations don't affect other destinations
   * - Errors are logged but don't throw to maintain app stability
   *
   * @template N - The event name (must be a key of EventPayloads)
   * @param eventName - The name of the event to track (e.g., 'ask_question', 'pageview')
   * @param payload - Optional payload data specific to the event type
   *
   * @example
   * ```tsx
   * // Track a simple event
   * track('property_created');
   *
   * // Track an event with payload
   * track('ask_question', { feature: 'ask' });
   *
   * // Track a pageview with path and title
   * track('pageview', { path: '/dashboard', title: 'Dashboard' });
   * ```
   */
  const track: TrackFn = useCallback(
    (eventName, params) => {
      debugLogger('track', eventName, params);
      Analytics.track(eventName, params);
    },
    [debugLogger]
  );

  /**
   * Track a pageview
   *
   * Specialized function for tracking page navigation events. Automatically
   * includes base payload properties like feature context when available.
   *
   * **Common Use Cases:**
   * - Route changes in single-page applications
   * - Page loads in multi-page applications
   * - Navigation tracking for user journey analysis
   *
   * @param payload - Pageview data including path and title
   * @param payload.path - The URL path of the page (e.g., '/dashboard', '/properties/123')
   * @param payload.title - The page title for display in analytics
   * @param payload.params - Optional parameters like audit context
   *
   * @example
   * ```tsx
   * // Basic pageview tracking
   * pageview({ path: '/dashboard', title: 'Dashboard' });
   *
   * // Pageview with additional context
   * pageview({
   *   path: '/audits/123',
   *   title: 'Lease Audit Details',
   *   params: { audit_context: 'lease_review' }
   * });
   * ```
   */
  const pageview: PageViewFn = useCallback(
    (params) => {
      debugLogger('pageview', params);
      Analytics.pageview(params);
    },
    [debugLogger]
  );

  /**
   * Set user properties
   *
   * Updates user attributes/properties across all analytics destinations.
   * Useful for segmenting users, tracking user state, or storing user preferences.
   *
   * **Best Practices:**
   * - Use low-cardinality values for better analytics performance
   * - Avoid storing PII (Personally Identifiable Information)
   * - Set properties early in the user session for consistent tracking
   * - Use consistent property names across your application
   *
   * **Common Properties:**
   * - `userType`: User role (admin, user, etc.)
   * - `tenantId`: Organization/tenant identifier
   * - `featureFlags`: Active feature flags for the user
   * - `subscriptionTier`: User's subscription level
   *
   * @param properties - Object containing user properties to set
   * @param properties[key] - Property value (string, number, boolean, or object)
   *
   * @example
   * ```tsx
   * // Set basic user properties
   * setUser({
   *   userType: 'admin',
   *   tenantId: 'tenant_123',
   *   subscriptionTier: 'premium'
   * });
   *
   * // Set feature-specific properties
   * setUser({
   *   hasLeaseAudits: true,
   *   maxProperties: 100,
   *   preferredCurrency: 'USD'
   * });
   * ```
   */
  const setUser = useCallback(
    (properties: Record<string, unknown>) => {
      debugLogger('setUser', properties);
      Analytics.setUser(properties);
    },
    [debugLogger]
  );

  /**
   * Configure analytics for internal users
   *
   * This method should be called after user authentication when an internal user is detected.
   * In development/staging: No change needed (console already enabled)
   * In production: Replaces external destinations with console only
   *
   * **Note:** This method can be called multiple times safely.
   * **Note:** Currently uses is_admin as a proxy for internal users. Consider adding
   * a dedicated is_internal field to the user model for more precise control.
   *
   * @param properties - User properties to set after configuring for internal users
   *
   * @example
   * ```tsx
   * // After user authentication
   * if (user.is_admin) { // or user.is_internal when available
   *   setInternalUser({
   *     id: user.id,
   *     admin: user.is_admin,
   *     saas_tenant_id: user.saas_tenant_id
   *   });
   * }
   * ```
   */
  const setInternalUser = useCallback(
    async (properties: Record<string, unknown>) => {
      debugLogger('setInternalUser', properties);
      await Analytics.setInternalUser(properties);
    },
    [debugLogger]
  );

  return {
    track,
    pageview,
    setUser,
    setInternalUser,
  };
};
